import {
  Screenshot as ViewerScreenshot,
  Style as ViewerStyle,
} from '@swd_admin/cdc-viewer';
import {
  BOMOptionInstance,
  BOMStyleInstance,
  Currency,
  DevelopersService,
  Option,
  PostConfigurationResponse,
  Screenshot as BackendScreenshot,
  StyleInstance,
  Vector2d,
} from 'api';
import { ROUTES } from 'helpers/constants';
import { debounce } from 'helpers/util';
import { isGarageProduct, useAppState } from 'state/appstate/appstate';
import { useNotificationStore } from 'state/notificationstate/notificationstate';
import { useUiState } from 'state/ui-state/ui-state';
import { useViewerState } from 'state/viewer-state/viewer-state';
import { ExternalFeature } from '@swd_admin/cdc-viewer/dist/types/models/option';
import { getDefaultConfigurationGhModelJson } from './viewerActions';
import { OptionInstance } from '@swd_admin/cdc-viewer/dist/types';

const mapStateToStyleInstance = (): Omit<
  BOMStyleInstance | StyleInstance,
  'options'
> => {
  const appState = useAppState.getState();
  return {
    id: appState.styleProduct.style?.id!,
    productId: appState.styleProduct.product?.id!,
    depth: appState.uState?.dimensions?.depth!,
    width: appState.uState?.dimensions?.width!,
    wallHeight: appState.uState?.dimensions?.wallHeight!,
    overhang: appState.uState?.overhang!,
    siding: appState.uState?.materials.siding,
    roofing: appState.uState?.materials.roofing,
    trim: appState.uState?.materials.trim,
    // @ts-ignore
    hasConcretePad: appState.uState?.hasConcretePad || false,
  };
};

const mapStateToBOMStyleInstance = (
  state: ViewerStyle | undefined
): BOMStyleInstance => {
  const appState = useAppState.getState();
  const viewerState = useViewerState.getState().viewerState;
  const allOptions: Option[] = [
    ...(appState.styleProduct.style?.features || []),
    ...(appState.styleProduct.style?.options || []),
  ];

  return {
    ...mapStateToStyleInstance(),
    options: [
      ...(state?.instances
        ?.filter((d) => {
          const parentInstance = state?.instances.find(
            ({ id }) => id === d.parentId
          );
          const option = allOptions.find(
            ({ orderCode }) => parentInstance?.optionId === orderCode
          );

          const isDefaultStyleInstance =
            getDefaultConfigurationGhModelJson()?.instances?.some(
              ({ id }: OptionInstance) => d.id === id
            );
          const isDefaultFeatureOfOption = JSON.parse(
            option?.ghModelJson || '{}'
          )?.externalFeatures?.some(
            ({ id }: ExternalFeature) => id === d.optionId
          );

          return !isDefaultFeatureOfOption && !isDefaultStyleInstance;
        })
        ?.map((d) => ({
          definitionId: appState.dicts.shapeDiverIdToDbId![d.optionId],
          parentId: d.parentId,
          id: d.id,
        })) || []),
      ...(appState.uState?.bomOnlyOptions || []),
      ...(!isGarageProduct() &&
      (viewerState?.style?.renderParameters as any)?.style_floor_skirt === false
        ? [{ definitionId: 'OMF' } as BOMOptionInstance]
        : []),
    ],
  };
};

// Helper to find optionTypeId for a given definitionId
// First tries direct lookup, then searches for parent option that has this as a component
const findOptionTypeId = (
  definitionId: string,
  allOptions: Option[]
): string | undefined => {
  // Try direct lookup first
  const directOption = allOptions.find(({ id }) => id === definitionId);
  if (directOption) {
    return directOption.optionTypeId;
  }

  // Search for parent option that contains this as a component
  for (const opt of allOptions) {
    try {
      const ghModel = JSON.parse(opt.ghModelJson || '{}');
      if (ghModel.components?.some((comp: any) => comp.id === definitionId)) {
        return opt.optionTypeId;
      }
    } catch {
      // Ignore parse errors
    }
  }

  return undefined;
};

const mapStateToConfigurationStyleInstance = (
  state: ViewerStyle | undefined
): StyleInstance => {
  const viewerState = useViewerState.getState().viewerState;
  const appState = useAppState.getState();
  const allOptions: Option[] = [
    ...(appState.styleProduct.style?.features || []),
    ...(appState.styleProduct.style?.options || []),
  ];
  const defaultConfiguration =
    appState.styleProduct.style?.defaultConfiguration.style.ghModelJson &&
    JSON.parse(
      appState.styleProduct.style?.defaultConfiguration.style.ghModelJson
    );
  const storedComputedInstances = localStorage.getItem('internal-computedInstances');
  return {
    ...mapStateToStyleInstance(),
    ghModelJson: defaultConfiguration
      ? JSON.stringify({
          ...defaultConfiguration,
          renderParameters:
            state?.renderParameters || defaultConfiguration.renderParameters,
        })
      : '',
    options: [
      ...(state?.instances
        ?.filter(
          (d) =>
            !defaultConfiguration?.instances?.find(
              ({ id }: { id: string }) => id === d.id
            ) && allOptions.find(({ orderCode }) => d.optionId === orderCode)
        )
        ?.map((d) => {
          const option = allOptions.find(
            ({ orderCode }) => d.optionId === orderCode
          );
          return {
            definitionId: appState.dicts.shapeDiverIdToDbId![d.optionId],
            parentId: d.parentId,
            id: d.id,
            optionTypeId: option!.optionTypeId,
            localPosition: d.localPosition ||
            JSON.parse(storedComputedInstances || '{}').find((e: any) => e.id === d.id)?.localPosition || {} as Vector2d,
            ghModelJson: option!.ghModelJson as string,
          };
        }) || []),
      ...(appState.uState?.bomOnlyOptions || [])
        .map((d) => {
          const option = allOptions.find(({ id }) => d.definitionId === id);
          const optionTypeId = option?.optionTypeId || findOptionTypeId(d.definitionId, allOptions);
          return {
            ...d,
            optionTypeId: optionTypeId || '',
            localPosition: {} as Vector2d,
            ghModelJson: '',
          };
        })
        .filter((d) => d.optionTypeId), // Filter out options where we couldn't find optionTypeId
      ...(!isGarageProduct() &&
      (viewerState?.style?.renderParameters as any)?.style_floor_skirt === false
        ? [{ definitionId: 'OMF' } as any]
        : []),
    ],
    defaultBackground:
      appState.uState?.background ||
      appState.styleProduct.style?.defaultConfiguration.style.defaultBackground,
  };
};

const _updateBom = async () => {
  const appState = useAppState.getState();
  const { viewerState } = useViewerState.getState();
  if (!viewerState || !appState.uState) {
    return;
  }

  try {
    const newBom = await DevelopersService.getBillOfMaterials({
      style: mapStateToBOMStyleInstance({
        ...viewerState?.style,
      }),
      formatType: appState.uState.format,
      currency: appState.uState?.currency! || Currency.USD,
    });
    useUiState.getState().setBom(newBom);
  } catch (error) {
    useNotificationStore
      .getState()
      .setNotification('notifications.app.error.updateBom', 'error', 'small');
  }
};
export const updateBom = debounce(_updateBom, 500);

const mapScreenshotTypes = (s: ViewerScreenshot): BackendScreenshot => {
  return {
    base64EncodedImage: s.screenshot,
    side: s.side,
  };
};

export const saveConfiguration = async (
  portofolio: boolean,
  share = false
): Promise<PostConfigurationResponse | undefined> => {
  const { setViewerBusy, setSavingInProgress } = useUiState.getState();

  const { viewerState, viewer } = useViewerState.getState();
  setViewerBusy(true);
  setSavingInProgress({ portfolio: portofolio });
  const screenshots = (await viewer?.takeScreenshots())?.map((s) =>
    mapScreenshotTypes(s)
  );
console.log( JSON.stringify(mapStateToConfigurationStyleInstance(viewerState?.style)))
  try {
    return await DevelopersService.saveConfiguration({
      formatType: useAppState.getState().uState?.format!,
      style: mapStateToConfigurationStyleInstance(viewerState?.style),
      portofolio,
      share,
      screenshots: screenshots || [],
    });
  } catch (error) {
    console.log(error.message);

    throw new Error(error.message);
  } finally {
    setViewerBusy(false);
    setSavingInProgress(undefined);
  }
};

export const clearConfiguration = () => {
  window.location.pathname = ROUTES.PRODUCTS_SCREEN;
};

export const defaultConfiguration = () => {
  const { setViewer, viewer, clearSubscriptions } = useViewerState.getState();
  clearSubscriptions();
  viewer?.teardown();
  setViewer(undefined);
  useAppState.getState().setDefaultParameters();
};
